import { supabase } from "./supabase";

export type TypeRegle = "transaction" | "facture_achat";

/** Condition unitaire (libellé, montant ou type d'opération bancaire) */
export interface RegleCondition {
  field: "libelle" | "montant" | "type_operation";
  operator: "contains" | "starts_with" | "equals" | "gt" | "lt" | "gte" | "lte";
  value: string;
}

/** Conditions multiples liées par ET ou OU */
export interface RegleConditions {
  logic: "and" | "or";
  items: RegleCondition[];
}

export interface RegleCategorisation {
  id: string;
  dossier_id: string;
  nom?: string | null;
  field: string;
  operator: string;
  value: string;
  categorie_id: string | null;
  compte_charge_numero: string | null;
  taux_tva?: number | null;
  is_active: boolean;
  created_at: string;
  conditions?: RegleConditions | null;
  type_regle?: TypeRegle;
  priorite?: number;
  regime_tva?: string | null;
}

export interface CreateRegleInput {
  nom?: string | null;
  field?: string;
  operator?: string;
  value?: string;
  categorie_id?: string | null;
  compte_charge_numero?: string | null;
  taux_tva?: number | null;
  conditions?: RegleConditions | null;
  type_regle?: TypeRegle;
  priorite?: number;
  regime_tva?: string | null;
  is_active?: boolean;
}

function validateConditionsInput(input: CreateRegleInput, typeRegle: TypeRegle): void {
  if (typeRegle === "facture_achat") {
    const v = (input.value ?? "").trim().toLowerCase();
    if (!v) throw new Error("Le nom fournisseur est requis");
    return;
  }
  if (input.conditions?.items?.length) {
    const hasValid = input.conditions.items.some((c) => c.value != null && String(c.value).trim() !== "");
    if (!hasValid) throw new Error("Au moins une condition doit avoir une valeur");
  } else {
    const value = (input.value ?? "").trim();
    if (!value) throw new Error("La condition (mot-clé) est requise");
  }
}

/** Tri matching : priorité décroissante, puis création la plus récente. */
export function sortReglesPourMatching(regles: RegleCategorisation[]): RegleCategorisation[] {
  return [...regles].sort((a, b) => {
    const pa = Number(a.priorite) || 0;
    const pb = Number(b.priorite) || 0;
    if (pb !== pa) return pb - pa;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/** Crée une règle transaction (centre de règles — onglet Transactions). */
export async function createRegle(dossierId: string, input: CreateRegleInput): Promise<RegleCategorisation> {
  const typeRegle: TypeRegle = "transaction";
  validateConditionsInput(input, typeRegle);
  const useConditions = input.conditions?.items?.length;
  const firstVal = input.conditions?.items?.[0]?.value?.trim() ?? "";
  const legacy = {
    field: input.field ?? "libelle",
    operator: input.operator ?? "contains",
    value: useConditions
      ? firstVal || "multi"
      : input.operator === "equals"
        ? (input.value ?? "").trim().toLowerCase()
        : (input.value ?? "").trim(),
  };
  const { data, error } = await supabase
    .from("regles_categorisation")
    .insert({
      dossier_id: dossierId,
      nom: input.nom?.trim() || null,
      field: legacy.field,
      operator: legacy.operator,
      value: legacy.value,
      categorie_id: input.categorie_id || null,
      compte_charge_numero: input.compte_charge_numero?.trim() || null,
      taux_tva: input.taux_tva ?? null,
      conditions: useConditions ? input.conditions : null,
      type_regle: "transaction",
      priorite: input.priorite ?? 0,
      regime_tva: null,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();
  if (error) throw error;
  return data as RegleCategorisation;
}

export async function updateRegle(id: string, input: CreateRegleInput): Promise<void> {
  const typeRegle: TypeRegle = input.type_regle ?? "transaction";
  validateConditionsInput(input, typeRegle);

  if (typeRegle === "facture_achat") {
    const value = (input.value ?? "").trim().toLowerCase();
    const { error } = await supabase
      .from("regles_categorisation")
      .update({
        nom: input.nom?.trim() || null,
        field: "fournisseur_nom",
        operator: "equals",
        value,
        categorie_id: input.categorie_id ?? null,
        compte_charge_numero: input.compte_charge_numero?.trim() || null,
        taux_tva: input.taux_tva ?? null,
        conditions: null,
        type_regle: "facture_achat",
        priorite: input.priorite ?? 0,
        regime_tva: input.regime_tva ?? null,
        is_active: input.is_active ?? true,
      })
      .eq("id", id);
    if (error) throw error;
    return;
  }

  const useConditions = input.conditions?.items?.length;
  const firstVal = input.conditions?.items?.[0]?.value?.trim() ?? "";
  const legacy = {
    field: input.field ?? "libelle",
    operator: input.operator ?? "contains",
    value: useConditions
      ? firstVal || "multi"
      : input.operator === "equals"
        ? (input.value ?? "").trim().toLowerCase()
        : (input.value ?? "").trim(),
  };
  const { error } = await supabase
    .from("regles_categorisation")
    .update({
      nom: input.nom?.trim() || null,
      field: legacy.field,
      operator: legacy.operator,
      value: legacy.value,
      categorie_id: input.categorie_id || null,
      compte_charge_numero: input.compte_charge_numero?.trim() || null,
      taux_tva: input.taux_tva ?? null,
      conditions: useConditions ? input.conditions : null,
      type_regle: "transaction",
      priorite: input.priorite ?? 0,
      regime_tva: null,
      is_active: input.is_active ?? true,
    })
    .eq("id", id);
  if (error) throw error;
}

export interface CreateRegleFactureAchatOptions {
  priorite?: number;
  regime_tva?: "debits" | "encaissements" | null;
  nom?: string | null;
  taux_tva?: number | null;
  is_active?: boolean;
}

/** Règle facture d'achat : fournisseur (égalité insensible à la casse stockée en minuscules) → compte + TVA + régime TVA optionnel */
export async function createRegleFactureAchat(
  dossierId: string,
  fournisseurNom: string,
  compteChargeNumero: string,
  options?: CreateRegleFactureAchatOptions
): Promise<RegleCategorisation> {
  const value = fournisseurNom.trim().toLowerCase();
  if (!value || !compteChargeNumero.trim()) {
    throw new Error("Fournisseur et compte de charge requis");
  }
  const { data, error } = await supabase
    .from("regles_categorisation")
    .insert({
      dossier_id: dossierId,
      nom: options?.nom?.trim() || null,
      field: "fournisseur_nom",
      operator: "equals",
      value,
      categorie_id: null,
      compte_charge_numero: compteChargeNumero.trim(),
      taux_tva: options?.taux_tva ?? null,
      type_regle: "facture_achat",
      priorite: options?.priorite ?? 0,
      regime_tva: options?.regime_tva ?? null,
      is_active: options?.is_active ?? true,
    })
    .select()
    .single();
  if (error) throw error;
  return data as RegleCategorisation;
}

export async function fetchRegles(dossierId: string | null): Promise<RegleCategorisation[]> {
  if (!dossierId) return [];
  const { data, error } = await supabase
    .from("regles_categorisation")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("priorite", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as RegleCategorisation[];
}

/** Nombre d'exécutions enregistrées par règle (pour badges UI). */
export async function fetchCountsExecutionsParRegle(
  dossierId: string
): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from("regle_executions")
    .select("regle_id")
    .eq("dossier_id", dossierId)
    .not("regle_id", "is", null);
  if (error) throw error;
  const out: Record<string, number> = {};
  for (const row of data ?? []) {
    const id = (row as { regle_id: string }).regle_id;
    if (!id) continue;
    out[id] = (out[id] ?? 0) + 1;
  }
  return out;
}

export async function updatePrioritesRegles(dossierId: string, orderedIds: string[]): Promise<void> {
  const n = orderedIds.length;
  for (let i = 0; i < n; i++) {
    const priorite = (n - i) * 10;
    const { error } = await supabase
      .from("regles_categorisation")
      .update({ priorite })
      .eq("id", orderedIds[i]!)
      .eq("dossier_id", dossierId);
    if (error) throw error;
  }
}

export async function toggleRegleActive(id: string, isActive: boolean): Promise<void> {
  const { error } = await supabase.from("regles_categorisation").update({ is_active: isActive }).eq("id", id);
  if (error) throw error;
}

export type TransactionContextRegle = {
  libelle?: string | null;
  montant_ttc?: number | null;
  fournisseur_nom?: string | null;
  fournisseur_id?: string | null;
  /** `sortie` = débit banque (dépense), `entree` = crédit banque */
  type?: "entree" | "sortie" | null;
};

function evaluateCondition(cond: RegleCondition, ctx: { libelle: string; montant: number; txType: "entree" | "sortie" | null }): boolean {
  const val = String(cond.value ?? "").trim();
  if (!val) return false;

  if (cond.field === "libelle") {
    const lib = (ctx.libelle ?? "").toLowerCase();
    const v = cond.operator === "equals" ? val.toLowerCase() : val.toLowerCase();
    if (cond.operator === "contains") return lib.includes(v);
    if (cond.operator === "starts_with") return lib.startsWith(v);
    if (cond.operator === "equals") return lib === v;
    return false;
  }

  if (cond.field === "montant") {
    const amount = Math.abs(ctx.montant ?? 0);
    const num = parseFloat(val.replace(/\s/g, "").replace(",", "."));
    if (Number.isNaN(num)) return false;
    switch (cond.operator) {
      case "equals":
        return Math.abs(amount - num) < 0.01;
      case "gt":
        return amount > num;
      case "lt":
        return amount < num;
      case "gte":
        return amount >= num;
      case "lte":
        return amount <= num;
      default:
        return false;
    }
  }

  if (cond.field === "type_operation") {
    if (ctx.txType == null) return false;
    const want = val.toLowerCase();
    if (want === "debit") return ctx.txType === "sortie";
    if (want === "credit") return ctx.txType === "entree";
    return false;
  }

  return false;
}

export function regleMatchesTransaction(regle: RegleCategorisation, transaction: TransactionContextRegle): boolean {
  const typeR = (regle.type_regle as TypeRegle | undefined) ?? (regle.field === "fournisseur_nom" ? "facture_achat" : "transaction");
  if (typeR === "facture_achat") return false;

  const libelle = (transaction.libelle ?? "").trim();
  const montant = Number(transaction.montant_ttc) || 0;
  const txType = transaction.type ?? null;
  const ctx = { libelle, montant, txType };

  const conds = regle.conditions;
  if (conds?.items?.length) {
    const results = conds.items.map((c) => evaluateCondition(c, ctx));
    return conds.logic === "and" ? results.every(Boolean) : results.some(Boolean);
  }

  if (regle.field !== "libelle") return false;
  const vraw = (regle.value ?? "").trim();
  if (!vraw) return false;
  const lib = libelle.toLowerCase();
  const v = regle.operator === "equals" ? vraw.toLowerCase() : vraw.toLowerCase();
  if (regle.operator === "contains") return lib.includes(v);
  if (regle.operator === "starts_with") return lib.startsWith(v);
  if (regle.operator === "equals") return lib === v;
  return false;
}

export function findMatchingRegleFactureAchat(
  facture: { fournisseur_nom?: string | null },
  regles: RegleCategorisation[]
): RegleCategorisation | null {
  const nom = (facture.fournisseur_nom ?? "").trim().toLowerCase();
  if (!nom) return null;
  const sorted = sortReglesPourMatching(regles);
  for (const r of sorted) {
    if (!r.is_active) continue;
    const typeR = (r.type_regle as TypeRegle | undefined) ?? (r.field === "fournisseur_nom" ? "facture_achat" : "transaction");
    if (typeR !== "facture_achat") continue;
    if (r.field !== "fournisseur_nom" || r.operator !== "equals" || !r.compte_charge_numero) continue;
    const val = (r.value ?? "").toLowerCase();
    if (val && nom === val) return r;
  }
  return null;
}

export function findMatchingRegleTransaction(
  transaction: TransactionContextRegle,
  regles: RegleCategorisation[]
): RegleCategorisation | null {
  const sorted = sortReglesPourMatching(regles);
  for (const r of sorted) {
    if (!r.is_active) continue;
    const typeR = (r.type_regle as TypeRegle | undefined) ?? (r.field === "fournisseur_nom" ? "facture_achat" : "transaction");
    if (typeR !== "transaction") continue;
    if (regleMatchesTransaction(r, transaction)) return r;
  }
  return null;
}

export type SourceCompteTransaction = "regle_mot_cle" | "regle_fournisseur" | "fournisseur_defaut" | null;

export type ResultatCompteTransaction = {
  compte: string | null;
  source: SourceCompteTransaction;
  regle?: RegleCategorisation;
};

export async function determinerCompteTransaction(
  dossierId: string,
  transaction: TransactionContextRegle,
  regles?: RegleCategorisation[]
): Promise<ResultatCompteTransaction> {
  const reglesList = regles ?? (await fetchRegles(dossierId));

  const regleMotCle = findMatchingRegleTransaction(transaction, reglesList);
  const compteMotCle = regleMotCle?.compte_charge_numero?.trim();
  if (regleMotCle && compteMotCle) {
    return { compte: compteMotCle, source: "regle_mot_cle", regle: regleMotCle };
  }

  if ((transaction.fournisseur_nom ?? "").trim()) {
    const regleFournisseur = findMatchingRegleFactureAchat({ fournisseur_nom: transaction.fournisseur_nom }, reglesList);
    const compteFournisseur = regleFournisseur?.compte_charge_numero?.trim();
    if (regleFournisseur && compteFournisseur) {
      return { compte: compteFournisseur, source: "regle_fournisseur", regle: regleFournisseur };
    }
  }

  const fid = (transaction.fournisseur_id ?? "").trim();
  if (fid) {
    const { data, error } = await supabase
      .from("fournisseurs")
      .select("compte_charge_numero, compte_charge")
      .eq("id", fid)
      .eq("dossier_id", dossierId)
      .maybeSingle();
    if (!error && data) {
      const def =
        (data.compte_charge_numero as string | null | undefined)?.trim() ||
        (data.compte_charge as string | null | undefined)?.trim() ||
        null;
      if (def) return { compte: def, source: "fournisseur_defaut" };
    }
  }

  return { compte: null, source: null };
}

export type ModeExecutionRegle = "manuel" | "masse" | "auto";

export type ResultatAppliquerRegle = {
  compte: string | null;
  taux_tva: number | null;
};

/**
 * Trace l'application d'une règle (historique `regle_executions`).
 * À appeler après une action métier réussie (affectation, etc.).
 */
export async function appliquerRegle(
  regleId: string | null,
  entityType: "transaction" | "facture_achat",
  entityId: string,
  dossierId: string,
  mode: ModeExecutionRegle,
  resultat: ResultatAppliquerRegle
): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  const { error } = await supabase.from("regle_executions").insert({
    regle_id: regleId,
    dossier_id: dossierId,
    entity_type: entityType,
    entity_id: entityId,
    compte_applique: resultat.compte,
    taux_tva_applique: resultat.taux_tva,
    applique_par: uid,
    mode,
  });
  if (error) throw error;
}

const MOTS_EXCLUS_REGLE_LIBELLE = new Set([
  "virement",
  "paiement",
  "paiements",
  "transfert",
  "retrait",
  "depot",
  "dépôt",
  "mobile",
  "money",
  "cm",
  "ci",
  "cfb",
  "cfa",
  "fcfa",
  "du",
  "le",
  "la",
  "les",
  "des",
  "sur",
  "chez",
  "par",
  "une",
  "pour",
  "avec",
]);

export function extraireMotClePourRegle(libelle: string): string {
  const raw = (libelle ?? "").trim();
  if (!raw) return "";
  const tokens = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(/[^a-z0-9]+/i)
    .filter((w) => w.length > 3 && !MOTS_EXCLUS_REGLE_LIBELLE.has(w));
  if (tokens.length === 0) {
    const short = raw.split(/\s+/).find((w) => w.replace(/\W/g, "").length > 2);
    return (short ?? raw).slice(0, 40);
  }
  tokens.sort((a, b) => b.length - a.length);
  return tokens[0]!.slice(0, 60);
}
